import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';

/**
 * Enhanced sticky-add-to-cart for Horizon theme.
 *
 * Works with the existing product-information.liquid HTML — no new refs required.
 *
 * Enhancements over the original:
 * 1. Mobile CTA text restored — shows "أضف إلى السلة" + price on mobile (not just icon)
 * 2. Scroll-to-reviews on tapping the product title/variant area
 * 3. Double-tap / rapid-click prevention (data-loading guard)
 * 4. Entrance attention pulse on first appearance (respects prefers-reduced-motion)
 * 5. Swipe-down to dismiss on mobile, re-shows on scroll-up
 * 6. dataLayer / GA4 event push on sticky bar impression and add-to-cart click
 * 7. Accessible: aria-hidden toggled on show/hide
 */

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id]
 * @property {string} [title]
 * @property {boolean} [available]
 * @property {Object} [featured_media]
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar
 * @property {HTMLButtonElement} addToCartButton
 * @property {HTMLElement} quantityDisplay
 * @property {HTMLElement} quantityNumber
 * @property {HTMLImageElement} productImage
 */

class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  /** @type {boolean} */
  #hasPlayedEntrance = false;

  /** @type {boolean} */
  #prefersReducedMotion = false;

  /** @type {number} */
  #touchStartY = 0;

  /** @type {boolean} */
  #swipeDismissed = false;

  connectedCallback() {
    super.connectedCallback();

    this.#prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.#setupIntersectionObserver();
    this.#setupSwipeToDismiss();
    this.#setupTitleReviewsScroll();
    this.#getInitialQuantity();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#abortController.abort();
    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
  }

  // ─── Intersection Observer ─────────────────────────────────────────

  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (!footer) return;

    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      if (!entry.isIntersecting && !this.#isStuck) {
        const rect = entry.target.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top < 0) {
          this.#swipeDismissed = false;
          this.#showStickyBar();
        }
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hiddenByBottom = false;
        this.#hideStickyBar();
      }
    });

    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          const rect = buyButtonsBlock.getBoundingClientRect();
          if (rect.bottom < 0 || rect.top < 0) {
            this.#hiddenByBottom = false;
            this.#showStickyBar();
          }
        }
      },
      { rootMargin: '200px 0px 0px 0px' }
    );

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#mainBottomObserver.observe(footer);
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
  }

  // ─── Swipe to Dismiss ──────────────────────────────────────────────

  /**
   * On mobile: swipe down on the bar dismisses it.
   * It reappears automatically once the user scrolls back up 80px.
   */
  #setupSwipeToDismiss() {
    const { signal } = this.#abortController;
    const { stickyBar } = this.refs;

    stickyBar.addEventListener('touchstart', (e) => {
      if (e.touches.length !== 1) return;
      this.#touchStartY = e.touches[0].clientY;
    }, { signal, passive: true });

    stickyBar.addEventListener('touchend', (e) => {
      if (e.changedTouches.length !== 1) return;
      const deltaY = e.changedTouches[0].clientY - this.#touchStartY;
      // Downward swipe > 40px on mobile only
      if (deltaY > 40 && window.innerWidth < 750 && this.#isStuck) {
        this.#swipeDismissed = true;
        this.#hideStickyBar();
        this.#watchForScrollUp();
      }
    }, { signal });
  }

  #watchForScrollUp() {
    let lastScrollY = window.scrollY;
    const reshowController = new AbortController();

    window.addEventListener('scroll', () => {
      const currentScrollY = window.scrollY;
      if (lastScrollY - currentScrollY > 80 && this.#swipeDismissed) {
        reshowController.abort();
        this.#swipeDismissed = false;

        const productForm = this.#getProductForm();
        const buyButtonsBlock = productForm?.closest('.buy-buttons-block');
        if (buyButtonsBlock) {
          const rect = buyButtonsBlock.getBoundingClientRect();
          if (rect.bottom < 0 || rect.top < 0) this.#showStickyBar();
        }
      }
      lastScrollY = currentScrollY;
    }, { signal: reshowController.signal, passive: true });

    // Clean up if component disconnects
    this.#abortController.signal.addEventListener('abort', () => reshowController.abort());
  }

  // ─── Tap Title → Scroll to Reviews ────────────────────────────────

  /**
   * Tapping the product title/info area in the sticky bar
   * smooth-scrolls to the Judge.me reviews section.
   */
  #setupTitleReviewsScroll() {
    const { signal } = this.#abortController;

    // The info area = title + variant text
    const infoEl = this.refs.stickyBar.querySelector('.sticky-add-to-cart__info');
    if (!infoEl) return;

    infoEl.style.cursor = 'pointer';
    infoEl.setAttribute('title', 'عرض التقييمات');
    infoEl.setAttribute('role', 'button');
    infoEl.setAttribute('tabindex', '0');

    const scrollToReviews = () => {
      const reviewsSection =
        document.querySelector('#judgeme_product_reviews') ||
        document.querySelector('.jdgm-widget') ||
        document.querySelector('.jdgm-rev-widg') ||
        document.querySelector('[data-reviews-section]') ||
        document.querySelector('.spr-container');

      if (!reviewsSection) return;

      const offset = this.refs.stickyBar.offsetHeight + 12;
      const targetY = reviewsSection.getBoundingClientRect().top + window.scrollY - offset;

      window.scrollTo({
        top: targetY,
        behavior: this.#prefersReducedMotion ? 'auto' : 'smooth',
      });
    };

    infoEl.addEventListener('click', scrollToReviews, { signal });
    infoEl.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); scrollToReviews(); }
    }, { signal });
  }

  // ─── Add to Cart ───────────────────────────────────────────────────

  handleAddToCartClick = async () => {
    if (!this.#targetAddToCartButton) return;

    // Prevent rapid double-taps
    if (this.refs.addToCartButton.dataset.loading === 'true') return;
    this.refs.addToCartButton.dataset.loading = 'true';

    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();

    // Analytics
    this.#pushAnalytics('sticky_add_to_cart_click', {
      quantity: this.#currentQuantity,
      variant_id: this.dataset.currentVariantId,
      product_id: this.dataset.productId,
    });

    const cartIcon = document.querySelector('.header-actions__cart-icon');

    if (this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    if (!cartIcon || !this.refs.addToCartButton || !this.refs.productImage) {
      this.refs.addToCartButton.dataset.loading = 'false';
      return;
    }

    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));
    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty('background-image', `url(${this.refs.productImage.src})`);
    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = this.refs.productImage;
    flyToCartElement.destination = cartIcon;
    document.body.appendChild(flyToCartElement);

    await onAnimationEnd([this.refs.addToCartButton, flyToCartElement]);
    this.refs.addToCartButton.dataset.loading = 'false';

    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 800);
  };

  // ─── Variant Handlers ──────────────────────────────────────────────

  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;
    const newStickyAddToCart = event.detail.data.html.querySelector('sticky-add-to-cart');
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
    const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

    this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
    this.dataset.variantAvailable = variantAvailable;

    if (variant && variant.id) this.dataset.currentVariantId = variant.id;

    const productForm = this.#getProductForm();
    if (productForm) {
      this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    }

    if (variant == null) this.#handleVariantUnavailable();

    // Re-attach info scroll after morph replaces DOM
    this.#setupTitleReviewsScroll();
    this.#updateButtonText();
  };

  #handleVariantSelected = (event) => {
    const variantId = event.detail.resource?.id;
    if (!variantId) return;
    this.dataset.currentVariantId = variantId;
  };

  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => option.value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  #handleCartAddComplete = (_event) => {
    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = 'false';
    }
    this.refs.addToCartButton.dataset.loading = 'false';
  };

  #handleQuantityUpdate = (event) => {
    if (event.detail.cartLine) return;
    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  // ─── Show / Hide ───────────────────────────────────────────────────

  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
    stickyBar.setAttribute('aria-hidden', 'false');

    // One-time entrance pulse to draw attention
    if (!this.#hasPlayedEntrance && !this.#prefersReducedMotion) {
      this.#hasPlayedEntrance = true;
      stickyBar.dataset.entrance = 'true';
      this.#animationTimeout = setTimeout(() => {
        delete stickyBar.dataset.entrance;
      }, 1000);
    }

    this.#pushAnalytics('sticky_bar_shown', {
      product_id: this.dataset.productId,
      first_time: !this.#hasPlayedEntrance,
    });
  }

  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
    stickyBar.setAttribute('aria-hidden', 'true');
  }

  // ─── Helpers ───────────────────────────────────────────────────────

  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;
    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;
    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;
    const available = !addToCartButton.disabled;
    quantityNumber.textContent = this.#currentQuantity.toString();

    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }

  /**
   * Push to dataLayer (GA4/GTM) if available
   * @param {string} action
   * @param {Object} data
   */
  #pushAnalytics(action, data = {}) {
    if (typeof window !== 'undefined' && Array.isArray(window.dataLayer)) {
      window.dataLayer.push({ event: action, ...data });
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
